import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { ApproveChangelogRequest, ApproveChangelogResponse } from "./types";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { displayToast } from "@/utils/toast";
import { jsonAuthHeaders } from "@/utils/headers";

interface UseApproveChangelogParams {
  changelogId: number;
}

export const useApproveChangelog = ({ changelogId }: UseApproveChangelogParams) => {
  const { workspaceId } = useParams();
  const authContext = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ApproveChangelogResponse, Error, ApproveChangelogRequest>({
    mutationFn: async (request: ApproveChangelogRequest) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000);

      try {
        const response = await fetch(
          `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/changelogs/${changelogId}/approval`,
          {
            method: "POST",
            headers: jsonAuthHeaders(authContext?.userToken ?? ""),
            body: JSON.stringify(request),
            signal: controller.signal,
          },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to process approval");
        }

        return response.json();
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      // Invalidate changelog queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["global-changelog", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["changelog"] });
      queryClient.invalidateQueries({ queryKey: ["changelog-pending-count", workspaceId] });

      // Show success toast
      if (variables.approve) {
        displayToast("Change approved successfully", ToastType.success);
      } else {
        displayToast("Change rejected", ToastType.success);
      }
    },
    onError: error => {
      displayToast(error.message || "Failed to process approval", ToastType.error);
    },
  });
};
